import * as superagent from 'superagent';
import Toast from './Toast';
import {
  AUTH_TOKEN_KEY,
  AUTH_USER_KEY,
  AUTH_REFRESH_TOKEN_KEY,
  remoteRoutes,
} from '../data/constants';
import { hasNoValue, hasValue } from '../components/inputs/inputHelpers';

export const getToken = (): string | null =>
  localStorage.getItem(AUTH_TOKEN_KEY);

type CallbackFunction = (data?: any) => void;
type ErrorCallback = (err: any, res: superagent.Response) => void;
type EndCallback = (data?: any) => void;

export const extractBadRequestErrorMessage = (message: any, errors: any) => {
  let msg = 'Invalid request format';
  if (typeof message === 'string') {
    msg = message;
  }
  if (Array.isArray(message)) {
    [msg] = message;
  }
  if (hasNoValue(msg) && hasValue(errors)) {
    [msg] = errors;
  }
  return msg;
};

// eslint-disable-next-line @typescript-eslint/default-param-last
export const handleError = (err: any = {}, res: superagent.Response) => {
  const authError = 22000987;
  const ajaxError = 22000987;
  const defaultMessage = 'Invalid request, please contact admin';
  if (res && res.unauthorized) {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_USER_KEY);
    Toast.error('Session expired. Please log in again.', authError);
    window.location.replace('/');
  } else if (res && res.forbidden) {
    // 403 means "not allowed to do this specific thing", not "your session
    // is invalid" — show the server's message and leave the user logged in.
    const { message } = res.body || {};
    Toast.error(message || "You don't have permission to do that", ajaxError);
  } else if (res && res.badRequest) {
    const { message, errors } = res.body;
    const msg = extractBadRequestErrorMessage(message, errors);
    Toast.error(msg || defaultMessage, ajaxError);
  } else if (
    (res && res.clientError) ||
    (res && res.notAcceptable) ||
    (res && res.error)
  ) {
    const { message } = res.body || {};
    Toast.error(message || defaultMessage, ajaxError);
  } else {
    const message = err.message || 'Unknown error, contact admin';
    const isConnectivityIssue =
      message.toLowerCase().indexOf('offline') !== -1 ||
      message.toLowerCase().indexOf('timeout') !== -1;
    const finalMessage = isConnectivityIssue
      ? "Can't reach server, Check connectivity"
      : message;
    Toast.error(finalMessage, ajaxError);
  }
};

// Was 0 (unbounded) — on a slow/lossy connection a request could hang
// indefinitely with zero feedback (stuck on "Signing in…" etc.) until some
// unpredictable, very long OS-level timeout eventually fired. A bounded
// timeout fails fast and clearly instead, so the user can just retry.
const timeout = 30000;
// eslint-disable-next-line @typescript-eslint/default-param-last
export const isAuthError = (err: any = {}, res: superagent.Response) => {
  if (err) {
    console.log(err);
    return false;
  }
  return (res && res.forbidden) || (res && res.unauthorized);
};

export const handleResponse =
  (
    callBack: CallbackFunction,
    errorCallBack?: ErrorCallback,
    endCallBack?: EndCallback,
  ) =>
  (err: any, res: superagent.Response) => {
    try {
      if (err || !res.ok) {
        if (errorCallBack) {
          errorCallBack(err, res);
        } else {
          handleError(err, res);
        }
      } else {
        callBack(res.body);
      }
    } catch (e) {
      console.error('Failed to process response', e);
    } finally {
      if (endCallBack) {
        endCallBack();
      }
    }
  };

// A 401 here doesn't necessarily mean the session is dead — it usually just
// means the 20-minute access token expired before useTokenRefresh's next
// 15-minute tick got a chance to run (browsers throttle setInterval in a
// backgrounded/inactive tab, so that tick can slip past the token's expiry).
// So on a 401/403 we try one silent refresh and retry the request before
// giving up — only a failed refresh (refresh token itself expired/invalid,
// e.g. the real 12h cap or a newer login elsewhere) results in a real logout.
let refreshInFlight: Promise<IRefreshOutcome> | null = null;

// A refresh attempt must never hang the calling request indefinitely —
// `timeout` above is 0 (unbounded) for normal API calls, but this one gets
// its own bounded timeout so a network hiccup can't leave a caller (e.g. a
// login attempt, see below) stuck forever.
const REFRESH_TIMEOUT_MS = 10000;

/**
 * A refresh can fail two very different ways, and conflating them is what
 * logs people out mid-task: the server can *reject* it (the session really
 * is over), or the request can simply not complete — a timeout, a dropped
 * connection, the API restarting during a deploy. Only the first means the
 * session is dead; the second should leave it alone and let the next
 * request try again.
 */
interface IRefreshOutcome {
  token: string | null;
  sessionDead: boolean;
}

const refreshAccessToken = (): Promise<IRefreshOutcome> => {
  if (refreshInFlight) return refreshInFlight;

  const refreshToken = localStorage.getItem(AUTH_REFRESH_TOKEN_KEY);
  // Nothing to refresh with — that session genuinely cannot continue.
  if (!refreshToken) {
    return Promise.resolve({ token: null, sessionDead: true });
  }

  refreshInFlight = new Promise<IRefreshOutcome>((resolve) => {
    superagent
      .post(remoteRoutes.refreshToken)
      .set('Accept', 'application/json')
      .set('Content-Type', 'application/json')
      .send({ refreshToken })
      .timeout(REFRESH_TIMEOUT_MS)
      .end((err, res) => {
        refreshInFlight = null;
        if (res?.ok && res.body?.token) {
          localStorage.setItem(AUTH_TOKEN_KEY, res.body.token);
          resolve({ token: res.body.token, sessionDead: false });
          return;
        }
        // Only an explicit rejection ends the session. No response at all
        // means we never got an answer, which is not the same thing.
        const rejected = !!res && (res.unauthorized || res.forbidden);
        resolve({ token: null, sessionDead: rejected });
      });
  });
  return refreshInFlight;
};

// Public, unauthenticated endpoints where a 401 means "wrong credentials /
// invalid token", not "my access token expired" — trying a silent refresh
// here makes no sense and only adds a (potentially slow) extra round-trip
// in front of the real error, e.g. making a wrong-password login attempt
// look like it's hanging before finally showing "invalid username/password".
const AUTH_RETRY_EXEMPT_URLS = new Set([
  remoteRoutes.login,
  remoteRoutes.register,
  remoteRoutes.refreshToken,
  remoteRoutes.forgotPassword,
  remoteRoutes.passwordResetComplete,
]);

// Runs a request built by `buildRequest`, and on 401 tries exactly one
// silent token refresh + retry before falling back to the normal error
// handling (which is what actually logs the user out). A 403 is a real
// permission decision, not an expired token — retrying it after a refresh
// would never change the answer, so it skips straight to normal error
// handling instead.
const sendWithAuthRetry = (
  url: string,
  buildRequest: () => superagent.SuperAgentRequest,
  callBack: CallbackFunction,
  errorCallBack?: ErrorCallback,
  endCallBack?: EndCallback,
  isRetry = false,
) => {
  buildRequest().end((err, res) => {
    const unauthorized = !!res && res.unauthorized;
    const exempt = AUTH_RETRY_EXEMPT_URLS.has(url);
    if (unauthorized && !isRetry && !exempt) {
      refreshAccessToken().then(({ token, sessionDead }) => {
        if (token) {
          sendWithAuthRetry(
            url,
            buildRequest,
            callBack,
            errorCallBack,
            endCallBack,
            true,
          );
          return;
        }
        // Couldn't reach the refresh endpoint: fail this one request quietly
        // rather than letting handleError read the original 401 as a dead
        // session and throw the user out over a momentary network problem.
        const onError =
          errorCallBack ?? (sessionDead ? undefined : () => undefined);
        handleResponse(callBack, onError, endCallBack)(err, res);
      });
      return;
    }
    handleResponse(callBack, errorCallBack, endCallBack)(err, res);
  });
};

export const get = (
  url: string,
  callBack: CallbackFunction,
  errorCallBack?: ErrorCallback,
  endCallBack?: EndCallback,
) => {
  sendWithAuthRetry(
    url,
    () =>
      superagent
        .get(url)
        .set('Authorization', `Bearer ${getToken()}`)
        .set('Accept', 'application/json')
        .timeout(timeout),
    callBack,
    errorCallBack,
    endCallBack,
  );
};
const cleanUp = (data: any = {}) =>
  Object.fromEntries(Object.entries(data).filter(([, v]) => v !== undefined));

export const search = (
  url: string,
  data: any,
  callBack: CallbackFunction,
  errorCallBack?: ErrorCallback,
  endCallBack?: EndCallback,
) => {
  const searchData = cleanUp(data);
  sendWithAuthRetry(
    url,
    () =>
      superagent
        .get(url)
        .set('Authorization', `Bearer ${getToken()}`)
        .set('Accept', 'application/json')
        .query(searchData)
        .timeout(timeout),
    callBack,
    errorCallBack,
    endCallBack,
  );
};

export const post = (
  url: string,
  data: any,
  callBack: CallbackFunction,
  errorCallBack?: ErrorCallback,
  endCallBack?: EndCallback,
) => {
  sendWithAuthRetry(
    url,
    () =>
      superagent
        .post(url)
        .set('Authorization', `Bearer ${getToken()}`)
        .set('Accept', 'application/json')
        .set('Content-Type', 'application/json')
        .send(data)
        .timeout(timeout),
    callBack,
    errorCallBack,
    endCallBack,
  );
};

export const postFile = (
  url: string,
  data: any,
  callBack: CallbackFunction,
  errorCallBack?: ErrorCallback,
  endCallBack?: EndCallback,
) => {
  sendWithAuthRetry(
    url,
    () =>
      superagent
        .post(url)
        .set('Authorization', `Bearer ${getToken()}`)
        .set('Accept', 'application/json')
        .send(data)
        .timeout(timeout),
    callBack,
    errorCallBack,
    endCallBack,
  );
};

export const put = (
  url: string,
  data: any,
  callBack: CallbackFunction,
  errorCallBack?: ErrorCallback,
  endCallBack?: EndCallback,
) => {
  sendWithAuthRetry(
    url,
    () =>
      superagent
        .put(url)
        .set('Authorization', `Bearer ${getToken()}`)
        .set('Content-Type', 'application/json')
        .set('Accept', 'application/json')
        .send(data)
        .timeout(timeout),
    callBack,
    errorCallBack,
    endCallBack,
  );
};

export const patch = (
  url: string,
  data: any,
  callBack: CallbackFunction,
  errorCallBack?: ErrorCallback,
  endCallBack?: EndCallback,
) => {
  sendWithAuthRetry(
    url,
    () =>
      superagent
        .patch(url)
        .set('Authorization', `Bearer ${getToken()}`)
        .set('Content-Type', 'application/json')
        .set('Accept', 'application/json')
        .send(data)
        .timeout(timeout),
    callBack,
    errorCallBack,
    endCallBack,
  );
};

export const del = (
  url: string,
  callBack: CallbackFunction,
  errorCallBack?: ErrorCallback,
  endCallBack?: EndCallback,
) => {
  sendWithAuthRetry(
    url,
    () =>
      superagent
        .delete(url)
        .set('Authorization', `Bearer ${getToken()}`)
        .set('Accept', 'application/json')
        .timeout(timeout),
    callBack,
    errorCallBack,
    endCallBack,
  );
};

export const downLoad = (
  url: string,
  callBack: CallbackFunction,
  errorCallBack?: ErrorCallback,
  endCallBack?: EndCallback,
) => {
  sendWithAuthRetry(
    url,
    () =>
      superagent
        .get(url)
        .set('Authorization', `Bearer ${getToken()}`)
        .responseType('blob'),
    callBack,
    errorCallBack,
    endCallBack,
  );
};

export const triggerDownLoad = (data: Blob, fileName = 'export.csv') => {
  const a = document.createElement('a');
  a.href = window.URL.createObjectURL(data);
  a.download = fileName;
  a.click();
};
